import { CONTACT, ASSETS } from "@/lib/constants";
import { ShieldCheck, TrendingDown, Clock3, CheckCircle2, HandCoins, BadgeCheck, Phone } from "lucide-react";
import { useApplyIntent } from "@/components/ApplyIntent";
import { useRateCheckRouter } from "@/components/RateCheckRouter";
import { useMLO } from "@/lib/mloContext";
import { resolveApplyUrl, applyPagePath } from "@/lib/applyTarget";

export default function Hero() {
  const { openIntent } = useApplyIntent();
  const { openRouter } = useRateCheckRouter();
  const mlo = useMLO();
  const applyUrl = resolveApplyUrl(mlo);
  const intentOpts = { applyUrl, mloSlug: mlo?.slug, mloName: mlo?.name, mloPhone: mlo?.phone };
  const heroFirstName = (mlo?.name || CONTACT.loanOfficer).split(" ")[0];
  const heroFullName = mlo?.name || CONTACT.loanOfficer;
  const heroPhoneDisplay = mlo?.phone || CONTACT.phoneDisplay;
  const heroPhoneHref = mlo?.phone
    ? `tel:${mlo.phone.replace(/[^+0-9]/g, "")}`
    : CONTACT.phoneHref;
  const yearsPart = mlo?.years_experience
    ? `a ${mlo.years_experience}+ year mortgage veteran`
    : "a licensed mortgage loan officer";
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 sm:pt-32 pb-20 sm:pb-28 grain-bg noise text-white"
    >
      {/* faint editorial grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="container-grc relative grid grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className="col-span-12 lg:col-span-7">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-lime/90"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
            Powered by NEXA Lending · NMLS #{CONTACT.companyNmls}
          </span>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-heading font-medium text-balance text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-[4.25rem] lg:leading-[1.02]"
          >
            A great mortgage answer is a{" "}
            <span className="italic text-lime">two-minute</span> question away.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-lg sm:text-xl text-white/75 leading-relaxed"
          >
            Great Rate Check pairs you with {heroFullName}, {yearsPart}{" "}
            at NEXA Lending, so you can explore your options and move forward
            with confidence — without the run-around.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              type="button"
              data-testid="hero-primary-cta"
              onClick={() => mlo ? openIntent("hero-primary-cta", intentOpts) : openRouter("hero-primary-cta")}
              className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
            >
              Check My Rate
              <span aria-hidden>→</span>
            </button>
            <a
              data-testid="hero-secondary-cta"
              href="#how"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium underline decoration-white/30 underline-offset-4 hover:decoration-lime transition-colors"
            >
              See how it works
            </a>
            {applyUrl ? (
              <a
                data-testid="hero-apply-link"
                href={applyPagePath(mlo)}
                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium underline decoration-white/30 underline-offset-4 hover:decoration-lime transition-colors"
              >
                Apply with {heroFirstName}
              </a>
            ) : (
              <a
                data-testid="hero-call-instead-link"
                href={heroPhoneHref}
                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium underline decoration-white/30 underline-offset-4 hover:decoration-lime transition-colors"
              >
                Call {heroFirstName} to get started
              </a>
            )}
          </div>

          {/* Prefer-to-talk phone line */}
          <a
            href={heroPhoneHref}
            data-testid="hero-phone-link"
            className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors group"
          >
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-white/10 group-hover:bg-lime/25 transition-colors">
              <Phone size={14} strokeWidth={2} className="text-lime" />
            </span>
            <span>
              Prefer to talk? Call {heroFirstName}{" "}
              <span className="font-semibold text-white tabular-nums group-hover:text-lime transition-colors">{heroPhoneDisplay}</span>
            </span>
          </a>

          {/* Trust badges */}
          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-white/70">
            <li className="inline-flex items-center gap-2" data-testid="trust-badge-secure">
              <ShieldCheck size={16} className="text-teal" strokeWidth={1.75} />
              Bank-level security · Your info stays private
            </li>
            <li className="inline-flex items-center gap-2" data-testid="trust-badge-rate">
              <TrendingDown size={16} className="text-lime" strokeWidth={1.75} />
              Personalized guidance
            </li>
            <li className="inline-flex items-center gap-2" data-testid="trust-badge-time">
              <Clock3 size={16} className="text-teal" strokeWidth={1.75} />
              About 2 minutes to start
            </li>
          </ul>
        </div>

        {/* Right visual: advisor snapshot (no rate numbers) */}
        <div className="col-span-12 lg:col-span-5 relative">
          <AdvisorCard />
        </div>
      </div>

      {/* soft bottom fade into linen */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-linen/30" />
    </section>
  );
}

function AdvisorCard() {
  const mlo = useMLO();
  const name = mlo?.name || "Jason Gray";
  const nmls = mlo?.nmls || CONTACT.nmls;
  const hasPortrait = !!mlo?.photoUrl || mlo?.slug === "jason" || !mlo;
  const photo = mlo?.photoUrl || (mlo?.slug === "jason" || !mlo ? ASSETS.headshotApply : null);
  const years = mlo?.years_experience;  // Only render pill when set (>0)
  const points = [
    { icon: <CheckCircle2 size={18} className="text-teal" />, label: "Purchase, refinance & cash-out scenarios" },
    { icon: <HandCoins size={18} className="text-teal" />, label: "Conventional, FHA, VA, Jumbo, DSCR & more" },
    { icon: <BadgeCheck size={18} className="text-teal" />, label: "One dedicated loan officer — start to close" },
  ];
  return (
    <div className="relative" data-testid="hero-advisor-card">
      <div className="absolute -inset-4 rounded-[2rem] bg-teal/20 blur-2xl" aria-hidden />
      <div className="relative rounded-3xl bg-white text-navy shadow-lift ring-1 ring-white/10 overflow-hidden">
        {/* Big hero portrait */}
        <div className="relative aspect-[1/1] w-full overflow-hidden bg-navy-deep">
          {hasPortrait && photo ? (
            <img
              src={photo}
              alt={`${name}, mortgage loan officer`}
              className="w-full h-full object-cover object-top"
              loading="eager"
              data-testid="hero-advisor-photo"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-deep via-[#0F2A47] to-[#17385e] p-14"
              data-testid="hero-advisor-photo-fallback"
            >
              <img
                src={ASSETS.logoMark}
                alt="Great Rate Check"
                className="w-full h-full object-contain opacity-90"
                loading="eager"
              />
            </div>
          )}
          {/* Bottom gradient overlay for name legibility */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/75 via-black/35 to-transparent" aria-hidden />
          {/* Name overlay on photo */}
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 flex items-end justify-between gap-3">
            <div>
              <p className="text-[10px] sm:text-xs font-bold tracking-[0.22em] uppercase text-lime/90">Your loan officer</p>
              <p className="font-heading font-medium text-2xl sm:text-3xl leading-tight text-white mt-1">{name}</p>
              <p className="text-xs text-white/80 mt-0.5">NMLS #{nmls} · NEXA Lending</p>
            </div>
            {years > 0 && (
              <span className="inline-flex items-center gap-1.5 shrink-0 bg-lime text-navy-deep text-[11px] font-bold px-3 py-1.5 rounded-full shadow">
                <BadgeCheck size={14} strokeWidth={2.5} />
                {years}+ yrs
              </span>
            )}
          </div>
        </div>

        {/* Info section below photo */}
        <div className="p-6 sm:p-7">
          <ul className="space-y-3">
            {points.map((p, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-navy">
                <span className="shrink-0">{p.icon}</span>
                <span>{p.label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 rounded-2xl bg-linen p-4 border border-linen-border">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-teal">Straight-shooting guidance</p>
            <p className="text-sm text-ink-700 mt-1 leading-relaxed">
              Mortgage guidance in plain English, without the sales pressure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
